import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, Building2, User, Check, Plus, AlertCircle } from 'lucide-react'
import { api } from '@/api/client'
import type { Organisation } from '@/api/types'
import { cn } from '@/lib/utils'

interface OrgSwitcherProps {
  collapsed?: boolean
}

export function OrgSwitcher({ collapsed }: OrgSwitcherProps) {
  const navigate = useNavigate()
  const [orgs, setOrgs] = useState<Organisation[]>([])
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    async function load() {
      try {
        const [list, activeId] = await Promise.all([
          api.getOrganisations(),
          api.getActiveOrgId(),
        ])
        setOrgs(list)
        setCurrentOrgId(activeId)
      } catch {
        // ignore
      }
    }
    load()
  }, [])

  // Subscribe to org changes
  useEffect(() => {
    return api.onOrgChange((id) => {
      setCurrentOrgId(id)
    })
  }, [])

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const currentOrg = orgs.find((o) => o.id === currentOrgId)

  if (!orgs.length) return null

  function handleSelect(org: Organisation) {
    api.switchOrg(org.id)
    setCurrentOrgId(org.id)
    setOpen(false)
    window.location.reload()
  }

  function handleCreateClick() {
    setOpen(false)
    navigate('/organisations/new')
  }

  if (collapsed) {
    return (
      <div className="border-b border-border px-2 py-3">
        <button
          onClick={() => setOpen(!open)}
          className="flex w-full items-center justify-center rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          title={currentOrg?.name || 'Switch organisation'}
        >
          {currentOrg?.personal ? (
            <User className="h-4 w-4" />
          ) : (
            <Building2 className="h-4 w-4" />
          )}
        </button>
        {open && (
          <div className="absolute left-[68px] top-auto z-[60] mt-[-36px] w-56 rounded-lg border border-border bg-card shadow-lg">
            <OrgList orgs={orgs} currentOrgId={currentOrgId} onSelect={handleSelect} />
            <div className="border-t border-border p-1">
              <button
                onClick={handleCreateClick}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <Plus className="h-3.5 w-3.5" />
                Create Organisation
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div ref={ref} className="relative border-b border-border px-3 py-3">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-accent"
      >
        {currentOrg?.personal ? (
          <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="flex-1 truncate text-left text-xs font-medium">
          {currentOrg?.name || 'Select org…'}
        </span>
        <ChevronDown className={cn('h-3 w-3 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute left-2 right-2 top-full z-[60] mt-1 rounded-lg border border-border bg-card shadow-lg">
          <OrgList orgs={orgs} currentOrgId={currentOrgId} onSelect={handleSelect} />
          <div className="border-t border-border p-1">
            <button
              onClick={handleCreateClick}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <Plus className="h-3.5 w-3.5" />
              Create Organisation
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function OrgList({
  orgs,
  currentOrgId,
  onSelect,
}: {
  orgs: Organisation[]
  currentOrgId: string | null
  onSelect: (org: Organisation) => void
}) {
  // Sort: personal first, then alphabetical
  const sorted = [...orgs].sort((a, b) => {
    if (a.personal && !b.personal) return -1
    if (!a.personal && b.personal) return 1
    return a.name.localeCompare(b.name)
  })

  return (
    <div className="max-h-64 overflow-y-auto py-1">
      {sorted.map((org) => {
        const isSuspended = !!(org as { suspended?: boolean }).suspended
        return (
          <button
            key={org.id}
            onClick={() => onSelect(org)}
            className={cn(
              'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-accent',
              org.id === currentOrgId && 'bg-primary/5',
              isSuspended && 'opacity-60',
            )}
          >
            {org.personal ? (
              <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            ) : (
              <Building2 className={cn('h-3.5 w-3.5 shrink-0', isSuspended ? 'text-destructive' : 'text-muted-foreground')} />
            )}
            <div className="flex-1 min-w-0">
              <p className="truncate text-xs font-medium">{org.name}</p>
              {org.personal && (
                <p className="text-[10px] text-muted-foreground">Personal</p>
              )}
              {isSuspended && !org.personal && (
                <p className="flex items-center gap-1 text-[10px] text-destructive">
                  <AlertCircle className="h-2.5 w-2.5" /> Suspended
                </p>
              )}
            </div>
            {org.id === currentOrgId && (
              <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
            )}
          </button>
        )
      })}
    </div>
  )
}
